/**
 * Ruhsat belgesi PDF üretici.
 * Yaklaşım:
 *   1. Şablon (webp) arkaplan olarak HTML div içine eklenir.
 *   2. Form verileri CSS Grid ile deterministik konumlandırılır.
 *   3. html2canvas DOM'u yakalar → jsPDF A4 olarak kaydeder.
 *
 * Absolute coordinate kullanılmaz; tüm yerleşim CSS Grid/Flex ile yönetilir.
 */

import html2canvas from 'html2canvas';
import { createPDF } from '../pdfUtils';

// A4 piksel boyutları (96 dpi referans, html2canvas scale=2 ile 192 dpi kalite)
const A4_W = 794;
const A4_H = 1123;

export async function ruhsatBelgesiOlustur(ruhsat, const_) {
  const el = buildDOM(ruhsat, const_);

  el.style.cssText = `
    position: fixed;
    top: -${A4_H + 100}px;
    left: -${A4_W + 100}px;
    width: ${A4_W}px;
    height: ${A4_H}px;
    overflow: hidden;
    z-index: -9999;
  `;
  document.body.appendChild(el);

  try {
    // Resmin yüklenmesini bekle
    const imgs = el.querySelectorAll('img');
    await Promise.all(
      Array.from(imgs).map(
        img =>
          img.complete
            ? Promise.resolve()
            : new Promise(res => {
                img.onload = res;
                img.onerror = res;
              })
      )
    );

    const canvas = await html2canvas(el, {
      scale: 2,
      useCORS: true,
      allowTaint: true,
      width: A4_W,
      height: A4_H,
      windowWidth: A4_W,
      windowHeight: A4_H,
      backgroundColor: '#ffffff',
      logging: false,
    });

    const doc = createPDF();
    const imgData = canvas.toDataURL('image/jpeg', 0.95);
    doc.addImage(imgData, 'JPEG', 0, 0, 210, 297);
    doc.save(`ruhsat_${ruhsat.rn || ruhsat.id}.pdf`);
  } finally {
    if (document.body.contains(el)) document.body.removeChild(el);
  }
}

/* ─────────────────────────────────────────────────────────────
   DOM oluşturucu
   ───────────────────────────────────────────────────────────── */
function buildDOM(ruhsat, const_) {
  const fmt = d => (d ? new Date(d).toLocaleDateString('tr-TR') : '—');

  const tcno     = ruhsat.tcno || ruhsat.vn || '';
  const adSoyad  = ruhsat.adi
    ? `${ruhsat.adi} ${ruhsat.soyadi || ''}`.trim()
    : ruhsat.sicil_unvani || ruhsat.unvan || '';
  const unvan    = ruhsat.unvan || '';
  const faaliyet = (ruhsat.ana_faaliyet_tam_adi || ruhsat.ana_faaliyet_adi || '').toUpperCase();
  const nace     = ruhsat.nace_kodu_no || '';
  const adres    = (ruhsat.isyeri_adresi || '').toUpperCase();

  const ada      = ruhsat.ada    || '';
  const pafta    = ruhsat.pafta  || '-';
  const parsel   = ruhsat.parsel || '';
  const acik     = ruhsat.acik_alan   != null ? `${Number(ruhsat.acik_alan).toFixed(2).replace('.', ',')} m²`   : '0,00 m²';
  const kapali   = ruhsat.kapali_alan != null ? `${Number(ruhsat.kapali_alan).toFixed(2).replace('.', ',')} m²` : '';

  const sinif      = ruhsat.sinif_adi || ruhsat.faaliyet_sinifi || '';
  const tarih      = fmt(ruhsat.onay_tarihi || ruhsat.basvuru_tarihi);
  const ruhsatNo   = ruhsat.rn || '';
  const baskanAdi  = const_.baskan_adi   || 'Hüseyin TAVLI';
  const baskanUnvan = const_.baskan_unvan || 'BELEDİYE BAŞKANI';

  const templateSrc = `${window.location.origin}/ruhsat_sablon.webp?t=${Date.now()}`;

  const wrap = document.createElement('div');
  wrap.innerHTML = `
<style>
  .rb-root {
    position: relative;
    width: ${A4_W}px;
    height: ${A4_H}px;
    font-family: Arial, Helvetica, sans-serif;
    font-size: 9px;
    color: #111;
    box-sizing: border-box;
    overflow: hidden;
  }
  .rb-bg {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    object-fit: fill;
    z-index: 1;
  }
  /* Veri katmanı — şablonun form alanı bölgesine oturur */
  .rb-data {
    position: absolute;
    /* Şablondaki ilk etiket satırına hizalama — piksel ayarı burada */
    top: 233px;
    left: 56px;
    right: 56px;
    z-index: 2;
    line-height: 1.4;
  }

  /* ── Ana form satırları ── */
  .rb-row {
    display: grid;
    grid-template-columns: 170px 12px minmax(0, 1fr);
    align-items: start;
    margin-bottom: 4.2px;
  }
  .rb-lbl  { font-weight: 700; white-space: nowrap; }
  .rb-col  { /* kolon karakteri */ }
  .rb-val  { overflow-wrap: break-word; word-break: break-word; min-width: 0; }

  /* ── İşyerinin Bulunduğu Yer bölümü ── */
  .rb-yer-grid {
    /* Ana row ile hizalanmış; yer label + 3 alt kolona bölünmüş */
    display: grid;
    grid-template-columns: 170px 12px minmax(0, 1fr);
    align-items: start;
    margin-bottom: 0;
  }
  .rb-yer-cols {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    column-gap: 6px;
  }
  .rb-mini {
    display: grid;
    grid-template-columns: max-content 8px minmax(45px, 1fr);
    align-items: start;
  }
  .rb-mini .rb-lbl { white-space: nowrap; }

  /* ── Açık/Kapalı alan alt satırı ── */
  .rb-alan-row {
    display: grid;
    /* label sütunu boş bırakılır, 3'lü grid hizası korunur */
    grid-template-columns: 170px 12px minmax(0, 1fr);
    align-items: start;
    margin-bottom: 4.2px;
  }
  .rb-alan-cols {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    column-gap: 6px;
  }

  /* ── İmza alanı ── */
  .rb-imza {
    display: flex;
    justify-content: flex-end;
    padding-right: 18px;
    margin-top: 14px;
  }
  .rb-imza-ic {
    text-align: center;
  }
  .rb-imza-adi   { font-weight: 700; font-size: 9px; }
  .rb-imza-unvan { font-weight: 600; font-size: 8.5px; }
</style>

<div class="rb-root">
  <img class="rb-bg" src="${templateSrc}" crossorigin="anonymous" alt="" />

  <div class="rb-data">

    <!-- T.C. Kimlik / Vergi No -->
    <div class="rb-row">
      <span class="rb-lbl">T.C. Kimlik/Vergi Kimlik No</span>
      <span class="rb-col">:</span>
      <span class="rb-val">${esc(tcno)}</span>
    </div>

    <!-- Adı Soyadı -->
    <div class="rb-row">
      <span class="rb-lbl">Adı Soyadı</span>
      <span class="rb-col">:</span>
      <span class="rb-val">${esc(adSoyad)}</span>
    </div>

    <!-- İş Yerinin Ünvanı -->
    <div class="rb-row">
      <span class="rb-lbl">İş Yerinin Ünvanı</span>
      <span class="rb-col">:</span>
      <span class="rb-val">${esc(unvan)}</span>
    </div>

    <!-- Faaliyet Konusu -->
    <div class="rb-row">
      <span class="rb-lbl">Faaliyet Konusu</span>
      <span class="rb-col">:</span>
      <span class="rb-val">${esc(faaliyet)}</span>
    </div>

    <!-- Nace Kodu -->
    <div class="rb-row">
      <span class="rb-lbl">Nace Kodu</span>
      <span class="rb-col">:</span>
      <span class="rb-val">${esc(nace)}</span>
    </div>

    <!-- İşyerinin Adresi -->
    <div class="rb-row">
      <span class="rb-lbl">İşyerinin Adresi</span>
      <span class="rb-col">:</span>
      <span class="rb-val">${esc(adres)}</span>
    </div>

    <!-- İşyerinin Bulunduğu Yer — Ada / Pafta / Parsel -->
    <div class="rb-yer-grid">
      <span class="rb-lbl">İşyerinin Bulunduğu Yer</span>
      <span class="rb-col">:</span>
      <div class="rb-yer-cols">
        <div class="rb-mini">
          <span class="rb-lbl">Ada No</span>
          <span>:</span>
          <span class="rb-val">${esc(ada)}</span>
        </div>
        <div class="rb-mini">
          <span class="rb-lbl">Pafta No</span>
          <span>:</span>
          <span class="rb-val">${esc(pafta)}</span>
        </div>
        <div class="rb-mini">
          <span class="rb-lbl">Parsel No</span>
          <span>:</span>
          <span class="rb-val">${esc(parsel)}</span>
        </div>
      </div>
    </div>

    <!-- Açık Alan / Kapalı Alan (Pafta ve Parsel kolonlarının altında) -->
    <div class="rb-alan-row" style="margin-bottom:4.2px;">
      <span></span><!-- label boş -->
      <span></span><!-- kolon boş -->
      <div class="rb-alan-cols">
        <div></div><!-- Ada No'nun altı boş -->
        <div class="rb-mini">
          <span class="rb-lbl">Açık Alan</span>
          <span>:</span>
          <span class="rb-val">${esc(acik)}</span>
        </div>
        <div class="rb-mini">
          <span class="rb-lbl">Kapalı Alan</span>
          <span>:</span>
          <span class="rb-val">${esc(kapali)}</span>
        </div>
      </div>
    </div>

    <!-- İşyerinin Sınıfı -->
    <div class="rb-row">
      <span class="rb-lbl">İşyerinin Sınıfı</span>
      <span class="rb-col">:</span>
      <span class="rb-val">${esc(sinif)}</span>
    </div>

    <!-- Ruhsat Tarih ve Sayısı -->
    <div class="rb-row">
      <span class="rb-lbl">Ruhsat Tarih ve Sayısı</span>
      <span class="rb-col">:</span>
      <span class="rb-val">${esc(tarih)} / ${esc(ruhsatNo)}</span>
    </div>

    <!-- Veriliş Tarihi -->
    <div class="rb-row">
      <span class="rb-lbl">Veriliş Tarihi</span>
      <span class="rb-col">:</span>
      <span class="rb-val">${esc(tarih)}</span>
    </div>

    <!-- Belediye Başkanı imza alanı -->
    <div class="rb-imza">
      <div class="rb-imza-ic">
        <div class="rb-imza-adi">${esc(baskanAdi)}</div>
        <div class="rb-imza-unvan">${esc(baskanUnvan)}</div>
      </div>
    </div>

  </div>
</div>
  `.trim();

  return wrap.firstElementChild;
}

/** HTML entity encoding — XSS önlemi */
function esc(str) {
  return String(str ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/* ─────────────────────────────────────────────────────────────
   İhtar ve kapatma kararı PDF'leri (değiştirilmedi)
   ───────────────────────────────────────────────────────────── */

export async function ihtarYazisiOlustur(ruhsat, const_, ihtarMetni) {
  const doc = createPDF();
  const W = 210;
  const fmt = d => d ? new Date(d).toLocaleDateString('tr-TR') : '—';

  doc.setFont('Arial', 'bold'); doc.setFontSize(14); doc.setTextColor(33);
  doc.text((const_.belediye_adi || 'ÜNYE BELEDİYE BAŞKANLIĞI').toUpperCase(), W / 2, 20, { align: 'center' });
  doc.setFontSize(10); doc.setFont('Arial', 'normal');
  doc.text('Ruhsat ve Denetim Müdürlügü', W / 2, 26, { align: 'center' });

  doc.setDrawColor(26, 35, 126); doc.setLineWidth(0.8);
  doc.line(14, 30, W - 14, 30);

  doc.setFontSize(14); doc.setFont('Arial', 'bold'); doc.setTextColor(26, 35, 126);
  doc.text('I H T A R N A M E', W / 2, 40, { align: 'center' });

  doc.setFont('Arial', 'normal'); doc.setFontSize(10); doc.setTextColor(33);
  doc.text(`Tarih: ${fmt(new Date())}`, W - 14, 50, { align: 'right' });

  doc.setFillColor(232, 234, 246);
  doc.rect(14, 54, W - 28, 28, 'F');
  let y = 60;
  const bilgi = (label, val) => {
    doc.setFont('Arial', 'bold'); doc.text(label + ':', 18, y);
    doc.setFont('Arial', 'normal'); doc.text(val || '—', 65, y);
    y += 6;
  };
  bilgi('İşyeri Ünvani', ruhsat.unvan);
  bilgi('Ruhsat No', ruhsat.rn);
  bilgi('İşyeri Adresi', ruhsat.isyeri_adresi);
  bilgi('Sahip / Yetkili', ruhsat.adi ? `${ruhsat.adi} ${ruhsat.soyadi}` : ruhsat.unvan);

  y = 90;
  doc.setFont('Arial', 'normal'); doc.setFontSize(10); doc.setTextColor(33);
  const metin = ihtarMetni || `Yukarida bilgileri yazili isyerinizde yapilan denetimde asagida belirtilen eksiklik/aykiriliklar tespit edilmistir. 3572 sayili İsyeri Acma ve Calisma Ruhsatlarina İliskin Kanun ve İlgili Yönetmelik uyarinca gerekli düzenlemelerin 30 gün icinde yapilmasi, aksi halde ruhsatinizin iptal edilecegi ve idari yaptirim uygulanacagi tarafiniza teblig olunur.`;
  const metinLines = doc.splitTextToSize(metin, W - 28);
  doc.text(metinLines, 14, y);
  y += metinLines.length * 6 + 10;

  doc.setFont('Arial', 'bold');
  doc.text('Teblig Eden:', 14, y + 20);
  doc.text('Teblig Alan:', W / 2 + 10, y + 20);
  doc.line(14, y + 40, 80, y + 40);
  doc.line(W / 2 + 10, y + 40, W - 14, y + 40);
  doc.setFont('Arial', 'normal'); doc.setFontSize(9);
  doc.text('Ad Soyad / Unvan / İmza', 14, y + 44);
  doc.text('Ad Soyad / İmza / Tarih', W / 2 + 10, y + 44);

  doc.save(`ihtar_${ruhsat.rn || ruhsat.id}.pdf`);
}

export async function kapatmaKarariOlustur(ruhsat, const_, yaptirim, aciklama) {
  const doc = createPDF();
  const W = 210;
  const fmt = d => d ? new Date(d).toLocaleDateString('tr-TR') : '—';

  doc.setFont('Arial', 'bold'); doc.setFontSize(14); doc.setTextColor(33);
  doc.text((const_.belediye_adi || 'ÜNYE BELEDİYE BAŞKANLIĞI').toUpperCase(), W / 2, 20, { align: 'center' });
  doc.setFontSize(10); doc.setFont('Arial', 'normal');
  doc.text('Ruhsat ve Denetim Müdürlügü', W / 2, 26, { align: 'center' });

  doc.setDrawColor(176, 0, 32); doc.setLineWidth(1.2);
  doc.line(14, 30, W - 14, 30);

  doc.setFillColor(255, 235, 238);
  doc.rect(14, 33, W - 28, 12, 'F');
  doc.setFont('Arial', 'bold'); doc.setFontSize(14); doc.setTextColor(176, 0, 32);
  doc.text('GECİCİ KAPATMA KARARI', W / 2, 41, { align: 'center' });

  doc.setTextColor(33); doc.setFont('Arial', 'normal'); doc.setFontSize(10);
  doc.text(`Karar Tarihi: ${fmt(yaptirim?.karar_tarihi || new Date())}    Karar No: ${yaptirim?.karar_no || '—'}`, W / 2, 52, { align: 'center' });

  doc.setFillColor(232, 234, 246); doc.rect(14, 56, W - 28, 30, 'F');
  let y = 62;
  const b = (l, v) => { doc.setFont('Arial','bold'); doc.text(l+':',18,y); doc.setFont('Arial','normal'); doc.text(v||'—',65,y); y+=6; };
  b('İşyeri Ünvani', ruhsat.unvan);
  b('Ruhsat No', ruhsat.rn);
  b('İşyeri Adresi', ruhsat.isyeri_adresi);
  b('Yetkili', ruhsat.adi ? `${ruhsat.adi} ${ruhsat.soyadi}` : ruhsat.unvan);

  y = 94;
  const kapatma = `3572 sayili İsyeri Acma ve Calisma Ruhsatlarina İliskin Kanun'un 6. maddesi uyarinca yukarida bilgileri yazili isyeri ${fmt(yaptirim?.kapatma_baslangic)} tarihinden itibaren GECİCİ OLARAK KAPATILMIŞTIR.`;
  const kLines = doc.splitTextToSize(kapatma, W - 28);
  doc.setFont('Arial', 'bold'); doc.setTextColor(176, 0, 32);
  doc.text(kLines, 14, y); y += kLines.length * 6 + 6;
  doc.setTextColor(33);

  if (aciklama) {
    doc.setFont('Arial', 'normal');
    const aLines = doc.splitTextToSize('Gerekce: ' + aciklama, W - 28);
    doc.text(aLines, 14, y); y += aLines.length * 6 + 6;
  }

  if (yaptirim?.muhur_no) {
    doc.setFillColor(255,235,238); doc.rect(14, y, W-28, 10, 'F');
    doc.setFont('Arial','bold'); doc.setTextColor(176,0,32);
    doc.text(`MÜHÜR NO: ${yaptirim.muhur_no}`, W/2, y+6, {align:'center'});
    y += 14;
  }

  if (yaptirim?.ceza_tutari) {
    doc.setFont('Arial','bold'); doc.setTextColor(33);
    doc.text(`İdari Para Cezasi: ${Number(yaptirim.ceza_tutari).toLocaleString('tr-TR')} TL`, 14, y);
    y += 8;
  }

  y = Math.max(y, 220);
  doc.setFont('Arial','bold'); doc.setTextColor(33);
  doc.text(`${const_.belediye_adi || 'Ünye Belediyesi'}`, W/2, y+15, {align:'center'});
  doc.text(const_.baskan || 'Belediye Baskani', W/2, y+22, {align:'center'});
  doc.line(W/2-30, y+35, W/2+30, y+35);
  doc.setFont('Arial','normal'); doc.setFontSize(9);
  doc.text('İmza / Mühür', W/2, y+39, {align:'center'});

  doc.save(`kapatma_karari_${ruhsat.rn || ruhsat.id}.pdf`);
}
